import { MouseEvent, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { MemCommonMentionKind } from "@mem-labs/common-editor";
import { clientEnvModule } from "@/modules/client-env";
import { electronModule } from "@/modules/electron";
import { windowModule } from "@/modules/window";
import { useFloatingAgentMentionLinkHandler } from "./floating-agent-mention-links";

const INTERNAL_MEM_HOSTNAMES = new Set(["mem.ai", "v2.mem.ai"]);
const SAFE_EXTERNAL_PROTOCOLS = new Set(["http:", "https:", "mailto:", "sms:"]);

const getUrl = (href: string): URL | null => {
  try {
    return new URL(href, windowModule.getOrigin());
  } catch (_error) {
    return null;
  }
};

const isInternalMemUrl = (url: URL): boolean => {
  return url.protocol === "mem:" || INTERNAL_MEM_HOSTNAMES.has(url.hostname);
};

// Internal note links point at /notes/:noteId (optionally under a mem: host).
// Route them through the mention path so they foreground the main window and
// open a new pane instead of reloading the app and replacing the current tab.
const NOTE_PATH_PATTERN = /\/notes\/([^/?#]+)/;

export const getNoteIdFromInternalUrl = (url: URL): string | null => {
  const match = NOTE_PATH_PATTERN.exec(url.pathname);
  return match?.[1] ?? null;
};

const isSafeExternalUrl = (url: URL): boolean => {
  return SAFE_EXTERNAL_PROTOCOLS.has(url.protocol);
};

/**
 * Converts same-origin and Mem-hosted links to React Router paths so web clicks
 * stay inside the app shell instead of reloading the document.
 */
export const getInternalRouteFromHref = (href: string): string | null => {
  const url = getUrl(href);
  if (!url) return null;

  const windowOrigin = windowModule.getOrigin();
  const isCurrentOrigin = windowOrigin !== undefined && url.origin === windowOrigin;
  const isMemHostedUrl = INTERNAL_MEM_HOSTNAMES.has(url.hostname);

  if (!isCurrentOrigin && !isMemHostedUrl) return null;

  return `${url.pathname}${url.search}${url.hash}`;
};

/**
 * Keeps links rendered inside the floating agent from navigating the widget
 * window. Desktop delegates to Electron so the main app owns app navigation;
 * web handles internal app links with React Router.
 */
export const useFloatingAgentLinkNavigation = () => {
  const navigate = useNavigate();
  const handleMentionLink = useFloatingAgentMentionLinkHandler();

  return useCallback(
    (href: string, target: HTMLElement, event: MouseEvent<Element>): boolean => {
      const url = getUrl(href);

      // Note links share the mention path: it foregrounds the main window and
      // opens a new pane, where navigateToInternalUrl reloads and replaces the
      // current tab.
      if (url && isInternalMemUrl(url)) {
        const noteId = getNoteIdFromInternalUrl(url);
        if (noteId) {
          handleMentionLink(MemCommonMentionKind.Note, noteId, target, event);
          return true;
        }
      }

      if (clientEnvModule.isDesktop() && url) {
        if (isInternalMemUrl(url)) {
          event.preventDefault();
          void electronModule.navigateToInternalUrl({ url });
          return true;
        }

        if (isSafeExternalUrl(url)) {
          event.preventDefault();
          void electronModule.navigateToExternalUrl({ url });
          return true;
        }
      }

      const internalRoute = getInternalRouteFromHref(href);
      if (!internalRoute) return false;

      event.preventDefault();
      navigate(internalRoute);
      return true;
    },
    [navigate, handleMentionLink]
  );
};
